import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { Search, X, Briefcase, MapPin, Zap, Clock, Building2, FileText, RotateCcw, GraduationCap, AlertCircle } from 'lucide-react';

const SearchFilterPanel = ({ filters: initialFilters, onSearch, onReset, isSearching, noResultsError }) => {
  const [filters, setFilters] = useState({
    jobTitle: '',
    location: '',
    skills: [],
    minExpYears: 0,
    maxExpYears: 0,
    industry: '',
    companyName: '',
    education: []
  });

  const [skillInput, setSkillInput] = useState('');
  const [eduInput, setEduInput] = useState('');

  useEffect(() => {
    if (initialFilters) {
      setFilters(prev => ({
        ...prev,
        ...initialFilters,
        skills: Array.isArray(initialFilters.skills) ? initialFilters.skills : [],
        education: Array.isArray(initialFilters.education) ? initialFilters.education : [],
        minExpYears: initialFilters.minExpYears || 0,
        maxExpYears: initialFilters.maxExpYears || 0,
      }));
    }
  }, [initialFilters]);

  const updateField = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const addSkill = () => {
    const s = skillInput.trim();
    if (s && !filters.skills.includes(s)) {
      updateField('skills', [...filters.skills, s]);
    }
    setSkillInput('');
  };

  const removeSkill = (skill) => {
    updateField('skills', filters.skills.filter(s => s !== skill));
  };

  const addEducation = () => {
    const e = eduInput.trim();
    if (e && !filters.education.includes(e)) {
      updateField('education', [...filters.education, e]);
    }
    setEduInput('');
  };

  const removeEducation = (edu) => {
    updateField('education', filters.education.filter(e => e !== edu));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!filters.jobTitle.trim()) return;
    onSearch(filters);
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 12 }} 
      animate={{ opacity: 1, y: 0 }} 
      className="flex flex-col h-full"
    >
      {/* Scrollable form body */}
      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-5 py-4 space-y-3 scrollbar-thin-light">
        {noResultsError && (
          <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-[13px] font-medium leading-relaxed flex gap-2.5 items-start mb-2 animate-fadeIn">
            <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5 animate-pulse" />
            <div>
              <p className="font-bold text-amber-900 mb-0.5">No Candidates Found</p>
              No candidates found matching the applied filters. Please adjust your criteria and try again with broader or more specific filters.
            </div>
          </div>
        )}

        {/* Job Title + Location — side by side */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
              <Briefcase className="w-3 h-3" /> Job Title <span className="text-red-400">*</span>
            </label>
            <input 
              type="text" value={filters.jobTitle} onChange={e => updateField('jobTitle', e.target.value)}
              placeholder="e.g. React.js Frontend Developer"
              className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20 transition-all"
            />
          </div>
          <div>
            <label className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
              <MapPin className="w-3 h-3" /> Location
            </label>
            <input 
              type="text" value={filters.location} onChange={e => updateField('location', e.target.value)}
              placeholder="e.g. Bangalore, Mumbai"
              className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20 transition-all"
            />
          </div>
        </div>

        {/* Skills */}
        <div>
          <label className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
            <Zap className="w-3 h-3" /> Skills
          </label>
          <div className="flex gap-1.5 mb-1.5">
            <input 
              type="text" value={skillInput} onChange={e => setSkillInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSkill(); } }}
              placeholder="Type a skill and press Enter"
              className="flex-1 px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20 transition-all"
            />
            <button type="button" onClick={addSkill} className="px-2.5 py-1.5 text-xs font-semibold text-[#007BFF] border border-[#007BFF]/30 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
              Add
            </button>
          </div>
          {filters.skills.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {filters.skills.map((skill, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-50 text-[#007BFF] text-xs font-medium border border-blue-100">
                  {skill}
                  <button type="button" onClick={() => removeSkill(skill)} className="hover:text-red-500 transition-colors">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Education / Institution — right after Skills */}
        <div>
          <label className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
            <GraduationCap className="w-3 h-3" /> Education / Institution
          </label>
          <div className="flex gap-1.5 mb-1.5">
            <input 
              type="text" value={eduInput} onChange={e => setEduInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addEducation(); } }}
              placeholder="e.g. IIT, BITS, NIT, Stanford"
              className="flex-1 px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20 transition-all"
            />
            <button type="button" onClick={addEducation} className="px-2.5 py-1.5 text-xs font-semibold text-[#007BFF] border border-[#007BFF]/30 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
              Add
            </button>
          </div>
          {filters.education.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {filters.education.map((edu, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-purple-50 text-purple-600 text-xs font-medium border border-purple-100">
                  {edu}
                  <button type="button" onClick={() => removeEducation(edu)} className="hover:text-red-500 transition-colors">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Experience Range */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
              <Clock className="w-3 h-3" /> Min Exp (yrs)
            </label>
            <input 
              type="number" min="0" max="40" value={filters.minExpYears || ''} 
              onChange={e => updateField('minExpYears', parseInt(e.target.value) || 0)}
              placeholder="0"
              className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20 transition-all"
            />
          </div>
          <div>
            <label className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
              Max Exp (yrs)
            </label>
            <input 
              type="number" min="0" max="40" value={filters.maxExpYears || ''} 
              onChange={e => updateField('maxExpYears', parseInt(e.target.value) || 0)}
              placeholder="0"
              className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20 transition-all"
            />
          </div>
        </div>

        {/* Industry */}
        <div>
          <label className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
            <Building2 className="w-3 h-3" /> Industry
          </label>
          <input 
            type="text" value={filters.industry} onChange={e => updateField('industry', e.target.value)}
            placeholder="e.g. Information Technology"
            className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20 transition-all"
          />
        </div>

        {/* Company Name */}
        <div>
          <label className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
            <Building2 className="w-3 h-3" /> Company (optional)
          </label>
          <input 
            type="text" value={filters.companyName} onChange={e => updateField('companyName', e.target.value)}
            placeholder="e.g. Google, Infosys"
            className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20 transition-all"
          />
        </div>
      </form>

      {/* Search Button */}
      <div className="flex-shrink-0 px-4 py-3 border-t border-gray-100 bg-[#FAFBFC]">
        <button 
          type="submit" onClick={handleSubmit}
          disabled={!filters.jobTitle.trim() || isSearching}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-[#007BFF] to-[#0062CC] hover:from-[#0062CC] hover:to-[#004FA3] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
        >
          {isSearching ? (
            <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Searching...</>
          ) : (
            <><Search className="w-4 h-4" /> Search Candidates</>
          )}
        </button>
      </div>
    </motion.div>
  );
};

export default SearchFilterPanel;
